import {Box, HBox} from '../../../../../src/components/core/view';
import React, {useCallback} from 'react';
import {
  SubstitutionItemParams,
  SubstitutionOptions,
} from '../../../../../src/itemDetails/types/itemReplacement';
import {
  useColorThemeType,
  useFontTheme,
} from '../../../../../src/appStyles/hooks/theme';

import {ActivityIndicator} from 'react-native';
import {CustomImage} from '../../../../../src/components/core/Image';
import {H5Text} from '../../../../../src/components/core/text';
import If from '@buncha/components/conditional/If';
import {ItemDetailsScreen} from '@buncha/itemDetails/navigation/types';
import {Modules} from '@buncha/utils/types';
import {PButton} from '../../../../../src/components/core/Button';
import {ShopperScreens} from '@buncha/home/navigation/types';
import {Touchable} from '@buncha/components/core/touchable';
import {navigation} from '@buncha/config/navigation';
import {scaleFonts} from '../../../../../src/appStyles/style/font';
import {styles} from './styles';
import {useGetLocale} from '@buncha/localization/hooks';
import {useItemReplacementContext} from '../../../../../src/itemDetails/hooks/itemReplacement';

interface SubstitutionOptionsCellProps {
  onPressHandler: (params: SubstitutionItemParams) => void;
  itemInfo?: SubstitutionOptions;
  loading?: boolean;
  runId: number;
  orderId?: number;
  showReplaceCTAWithCell?: boolean;
}

const SubstitutionOptionsCell: React.FC<
  SubstitutionOptionsCellProps
> = props => {
  const {state} = useItemReplacementContext();
  const orderItemIdToBeReplaced = state?.item?.id || 0;
  const itemToBeSubstituted = state?.item;
  const quantity = state?.item?.quantity || 0;
  const theme = useColorThemeType('main');
  const muli = useFontTheme('muli');
  const item = props?.itemInfo;
  const name = item?.name || '';
  const imageUrl = item?.itemImageUrl || '';
  const price = item?.price || 0;
  const priceToShow = (item?.price || 0) / 100;
  const catalogItemId = item?.catalogItemId || 0;
  const isCustomItem = item?.isCustomItem || false;
  const orderItem = item?.orderItem;
  const orderId = props?.orderId || orderItem?.orderId || 0;
  const position = orderItem?.position || 0;
  const substitutionItemId = item?.id || 0;
  const metaLoggingForKpi = item?.metaLoggingForKpi;
  const {button} =
    useGetLocale(Modules.itemDetails, ShopperScreens.ItemDetails) ?? {};

  const substituteParams: SubstitutionItemParams = {
    catalogItemId: catalogItemId,
    isCustomItem: isCustomItem,
    itemImageUrl: imageUrl,
    name: name,
    orderId: orderId,
    orderItemId: orderItemIdToBeReplaced,
    position: position,
    price: price,
    quantity: quantity,
    substitutedBy: JSON.stringify(itemToBeSubstituted),
    substitutionItemId: substitutionItemId,
    metaLoggingForKpi,
    upcInput: {
      upcOriginal: item?.upc,
    },
  };

  const onClick = useCallback(() => {
    navigation.navigate(ShopperScreens.ItemDetails, {
      screen: ItemDetailsScreen.ReplacementItemDetailsPage,
      params: {
        catalogItemId: item?.catalogItemId,
        storeIndex: state?.item?.order?.run?.store?.elasticSearchIndex,
        runId: props?.runId,
        quantity: quantity,
        catalogItem: item,
        orderItem: state?.item,
        storeId: state?.item?.order?.run?.store?.id,
        substitutionItemId: item?.id,
      },
    });
  }, [item, props?.runId, quantity, state?.item]);

  return (
    <React.Fragment>
      <HBox style={styles.parentBox}>
        <Touchable onPress={onClick} style={styles.imageBox}>
          <CustomImage style={styles.image} source={imageUrl} />
        </Touchable>
        <Box style={styles.textBox}>
          <HBox>
            <Touchable onPress={onClick} style={styles.namePriceBox}>
              <H5Text
                numberOfLines={2}
                alignText="left"
                style={{
                  color: theme?.text?.blue?.[300],
                  fontFamily: muli?.[700]?.normal,
                }}>
                {name}
              </H5Text>
              <H5Text
                alignText="left"
                style={{
                  color: theme?.text?.blue?.[300],
                  fontFamily: muli?.[700]?.normal,
                }}>
                $ {priceToShow}
              </H5Text>
            </Touchable>

            <If condition={props?.showReplaceCTAWithCell}>
              <PButton
                style={styles.replaceCTA}
                title={button?.replace}
                loadingIndicator={<ActivityIndicator />}
                disabledBgColor={theme?.background?.green?.[200]}
                titleStyle={{fontSize: scaleFonts(12)}}
                onPress={() => props?.onPressHandler(substituteParams)}
                isLoading={props?.loading}
                disabled={props?.loading}
              />
            </If>
          </HBox>
        </Box>
      </HBox>
      <If condition={!props?.showReplaceCTAWithCell}>
        <PButton
          style={[styles.replaceCTABottom]}
          title={button?.replace}
          loadingIndicator={<ActivityIndicator />}
          disabledBgColor={theme?.background?.green?.[200]}
          onPress={() => props?.onPressHandler(substituteParams)}
          isLoading={props?.loading}
          disabled={props?.loading}
        />
      </If>
    </React.Fragment>
  );
};

export default React.memo(SubstitutionOptionsCell);
